import { Parameter } from "domain/entities/parameter";
import { Permission } from "domain/entities/permission";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { RoleInputFilter } from "domain/models/role-input-filter";
import DropdownComponent from "presentation/components/Shared/DropdownComponent";
import { useFunctions } from "presentation/hooks/use-query-functions";
import {
  useCreatePermissionMutation,
  useUpdatePermissionMutation,
} from "presentation/hooks/use-query-permission";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useState } from "react";

/**
 * Propriedades para o componente `PermissionForm`.
 */
interface PermissionFormProps {
  /** A permissão atual a ser editada. Se `undefined`, o formulário entra em modo de criação. */
  currentPermission?: Permission | undefined;
  /** Função de callback executada após uma operação de salvar (criar/atualizar) bem-sucedida. */
  onSuccess: () => void;
  /** Referência ao componente `Toast` para exibir notificações. */
  toast: React.RefObject<Toast | null>;
}

/**
 * Componente de formulário para criar ou editar uma `Permission`.
 * É responsável por gerir o estado dos campos do formulário, validar os dados
 * e submetê-los através das mutações de `react-query`.
 * @param {PermissionFormProps} props - As propriedades do componente.
 */
const PermissionForm: React.FC<PermissionFormProps> = ({
  currentPermission,
  onSuccess,
  toast,
}) => {
  // Estado para controlar os dados do formulário
  const [permission, setPermission] = useState<Permission>({} as Permission);

  const { mutate: createPermission, isPending: isCreating } =
    useCreatePermissionMutation();
  const { mutate: updatePermission, isPending: isUpdating } =
    useUpdatePermissionMutation();

  const { data: rolesData } = useRoleByFilter({
    limit: 100,
    sortBy: "name",
    sortOrder: "asc",
  } as RoleInputFilter);
  const { data: functionsData } = useFunctions({
    limit: 100,
    sortBy: "label",
    sortOrder: "asc",
  } as BaseInputFilter);

  /**
   * Efeito que inicializa ou atualiza o estado do formulário (`permission`)
   * sempre que a `currentPermission` (prop) é alterada.
   * Essencial para carregar os dados no modo de edição.
   */
  useEffect(() => {
    setPermission(currentPermission || ({} as Permission));
  }, [currentPermission]);

  /**
   * Manipula os erros retornados pelas mutações, exibindo uma mensagem de erro específica ou genérica.
   */
  const handleError = (error: any) => {
    let message = "Ocorreu um erro inesperado.";

    // Verifica a mensagem de erro específica para permissão duplicada
    const errorMessage = error?.response?.data?.metadata?.errors[0] || "";
    if (
      typeof errorMessage === "string" &&
      errorMessage.includes("already exists")
    ) {
      message = "Já existe uma permissão para este Perfil e Função.";
    }

    toast.current?.show({
      severity: "error",
      summary: "Erro",
      detail: message,
      life: 3000,
    });
  };

  /**
   * Manipula o sucesso das mutações, exibindo uma mensagem de sucesso e
   * chamando o callback `onSuccess` para fechar o modal.
   */
  const handleSuccess = (isUpdate: boolean) => {
    toast.current?.show({
      severity: "success",
      summary: "Sucesso",
      detail: isUpdate
        ? "Permissão atualizada com sucesso."
        : "Permissão criada com sucesso.",
      life: 3000,
    });
    onSuccess(); // Fecha o modal
  };

  /**
   * Função chamada ao clicar no botão "Guardar".
   * Valida os campos obrigatórios e dispara a mutação de criar ou atualizar.
   */
  const onSave = () => {
    // Validação básica para garantir que os campos obrigatórios estão preenchidos
    if (!permission.roleId || !permission.functionId) {
      toast.current?.show({
        severity: "warn",
        summary: "Campos Obrigatórios",
        detail: "Por favor, selecione um Perfil e uma Função.",
        life: 3000,
      });
      return;
    }

    if (permission.id) {
      updatePermission(permission, {
        onSuccess: () => handleSuccess(true),
        onError: handleError,
      });
    } else {
      createPermission(permission, {
        onSuccess: () => handleSuccess(false),
        onError: handleError,
      });
    }
  };

  /**
   * Manipulador genérico para atualizar o estado de qualquer campo booleano (checkbox).
   * @param {keyof Permission} field - O nome do campo no objeto `Permission` a ser atualizado.
   * @param {boolean} checked - O novo valor booleano.
   */
  const handleCheckboxChange = (field: keyof Permission, checked: boolean) => {
    setPermission((prev) => ({
      ...prev,
      [field]: checked,
    }));
  };

  const roles = useMemo<Parameter[]>(() => {
    if (!rolesData?.data) return [];
    return rolesData.data.map((item) => ({
      id: item.id,
      value: item.name,
      type: "role",
    }));
  }, [rolesData]);

  /**
   * Memoiza a lista de funções formatada para o `DropdownComponent`,
   * evitando recálculos desnecessários a cada renderização.
   */
  const functions = useMemo<Parameter[]>(() => {
    if (!functionsData?.data) return [];
    return functionsData.data.map((item) => ({
      id: item.id,
      value: item.label,
      type: "function",
    }));
  }, [functionsData]);

  return (
    <div>
      <div className="default-grid header-spacing">
        <div className="default-2-cols">
          <DropdownComponent
            options={roles}
            label="Perfil"
            isValueId={true}
            fieldNameId="roleId"
            fieldName="roleId"
            isDisable={!!permission?.id}
            currentObject={permission}
            getValue={permission?.roleId || null}
            setValue={setPermission}
          />
        </div>
        <div className="default-2-cols">
          <DropdownComponent
            options={functions}
            label="Função"
            isValueId={true}
            fieldNameId="functionId"
            fieldName="functionId"
            isDisable={!!permission?.id}
            currentObject={permission}
            getValue={permission?.functionId || null}
            setValue={setPermission}
          />
        </div>
        <div className="default-1-col">
          <Checkbox
            inputId="allowSave"
            className="mr-2"
            checked={!!permission?.allowSave}
            onChange={(e) =>
              handleCheckboxChange("allowSave", e.checked ?? false)
            }
          />
          <label htmlFor="allowSave">Salvar</label>
        </div>

        <div className="default-1-col">
          <Checkbox
            inputId="allowEdit"
            className="mr-2"
            checked={!!permission?.allowEdit}
            onChange={(e) =>
              handleCheckboxChange("allowEdit", e.checked ?? false)
            }
          />
          <label htmlFor="allowEdit">Editar</label>
        </div>

        <div className="default-1-col">
          <Checkbox
            inputId="allowDelete"
            className="mr-2"
            checked={!!permission?.allowDelete}
            onChange={(e) =>
              handleCheckboxChange("allowDelete", e.checked ?? false)
            }
          />
          <label htmlFor="allowDelete">Deletar</label>
        </div>

        <div className="default-1-col">
          <Checkbox
            inputId="allowView"
            className="mr-2"
            checked={!!permission?.allowView}
            onChange={(e) =>
              handleCheckboxChange("allowView", e.checked ?? false)
            }
          />
          <label htmlFor="allowView">Visualizar</label>
        </div>
        <div className="default-4-cols button-align">
          <Button
            label="Guardar"
            onClick={onSave}
            className="button-size"
            disabled={isCreating || isUpdating}
          />
        </div>
      </div>
    </div>
  );
};
export default PermissionForm;
